import { supabase } from '@/lib/supabase';
import { pickErrorMessage } from '@/lib/utils/errorMessage';
import type { QuizPersistenceIdentity } from '@/lib/auth/quizPersistenceIdentity';
import { toRpcIdentityParams } from '@/lib/auth/rpcIdentityParams';

export interface HomepageBrowseTagRow {
  tag_id: number;
  slug: string;
  display_name: string;
  question_count: number;
}

export interface HomepageBrowseTagProgressRow {
  tag_id: number;
  answered_count: number;
  total_count: number;
  percent_answered: number;
}

export async function loadHomepageBrowseTagsConfig(): Promise<number[]> {
  const { data, error } = await supabase
    .from('homepage_browse_categories')
    .select('tag_id, sort_order')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []).map((row) => row.tag_id);
}

export async function saveHomepageBrowseTagsConfig(tagIds: number[]): Promise<void> {
  const payload = tagIds.map((tagId) => ({ tag_id: tagId }));
  const { error } = await supabase.rpc('admin_save_homepage_browse_categories', {
    p_tags: payload,
  });
  if (error) throw new Error(pickErrorMessage(error));
}

export async function fetchHomepageBrowseTags(): Promise<HomepageBrowseTagRow[]> {
  const { data, error } = await supabase.rpc('get_homepage_browse_categories');
  if (error) throw error;
  const parsed = data as unknown;
  return Array.isArray(parsed) ? (parsed as HomepageBrowseTagRow[]) : [];
}

export async function fetchHomepageBrowseTagProgress(
  identity: QuizPersistenceIdentity,
  tagIds: number[]
): Promise<HomepageBrowseTagProgressRow[]> {
  if (!tagIds.length) return [];
  const { data, error } = await supabase.rpc('get_homepage_browse_category_progress', {
    ...toRpcIdentityParams(identity),
    p_tag_ids: tagIds,
  });
  if (error) throw error;
  return (data ?? []) as HomepageBrowseTagProgressRow[];
}
